// src/pages/Register.js - Multi-step sign-up with optional "Skip for now" on profile step
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { setDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '../firebase';
import Navigation from '../components/Navigation';

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userType, setUserType] = useState('student');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [college, setCollege] = useState('');
  const [major, setMajor] = useState('');
  const [otherCollege, setOtherCollege] = useState('');
  const [otherMajor, setOtherMajor] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('');
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const type = urlParams.get('type');
    if (type === 'student' || type === 'company') setUserType(type);
  }, [location.search]);

  const colleges = [
    'Arizona State University', 'Boston University', 'Columbia University', 'Cornell University',
    'Duke University', 'Harvard University', 'MIT', 'New York University', 'Northwestern University',
    'Princeton University', 'Stanford University', 'UC Berkeley', 'UCLA', 'University of Chicago',
    'University of Michigan', 'University of Pennsylvania', 'University of Texas', 'Yale University', 'Other'
  ];
  const majors = [
    'Accounting', 'Biology', 'Business Administration', 'Chemistry', 'Computer Science', 'Economics',
    'Engineering', 'English', 'Finance', 'History', 'Marketing', 'Mathematics', 'Physics',
    'Political Science', 'Psychology', 'Sociology', 'Statistics', 'Other'
  ];
  const industries = [
    'Advertising', 'Aerospace', 'Agriculture', 'Automotive', 'Banking', 'Biotechnology', 'Construction',
    'Consulting', 'Education', 'Energy', 'Entertainment', 'Finance', 'Food & Beverage', 'Healthcare',
    'Hospitality', 'Information Technology', 'Insurance', 'Manufacturing', 'Media', 'Nonprofit',
    'Pharmaceuticals', 'Real Estate', 'Retail', 'Software', 'Telecommunications', 'Transportation', 'Other'
  ];

  const handlePhotoChange = (e) => {
    if (e.target.files[0]) {
      setProfilePhoto(e.target.files[0]);
      const reader = new FileReader();
      reader.onload = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(e.target.files[0]);
    }
  };
  const handleLogoChange = (e) => {
    if (e.target.files[0]) {
      setCompanyLogo(e.target.files[0]);
      const reader = new FileReader();
      reader.onload = () => setLogoPreview(reader.result);
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const createMinimalUserAndProfile = async (uid, emailVal) => {
    await setDoc(doc(db, 'users', uid), {
      email: emailVal || '',
      userType,
      createdAt: new Date(),
      profileCompleted: false
    });
    if (userType === 'student') {
      await setDoc(doc(db, 'studentProfiles', uid), {
        fullName: '',
        email: emailVal || '',
        college: '',
        major: '',
        photoURL: null,
        bio: '',
        skills: [],
        resume: null,
        portfolio: null,
        activeProjects: 0,
        completedProjects: 0
      });
    } else {
      await setDoc(doc(db, 'companyProfiles', uid), {
        companyName: '',
        industry: '',
        companyLogo: null,
        companyDescription: '',
        companyWebsite: '',
        location: '',
        companySize: ''
      });
    }
  };

  const handleSkip = async () => {
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    try {
      setLoading(true);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      await createMinimalUserAndProfile(user.uid, user.email);
      setTimeout(() => {
        navigate(userType === 'student' ? '/student/dashboard' : '/company/dashboard', { replace: true });
      }, 300);
    } catch (err) {
      setError(err.message || 'Failed to create account');
      setLoading(false);
    }
  };

  const handleNextStep = () => {
    if (step === 1) {
      if (!email.trim()) { setError('Email is required'); return; }
      if (!password.trim()) { setError('Password is required'); return; }
      if (password !== confirmPassword) { setError('Passwords do not match'); return; }
      if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    }
    if (step === 2) {
      if (userType === 'student') {
        if (!fullName.trim()) { setError('Full name is required'); return; }
        if (!college) { setError('Please select your college'); return; }
        if (college === 'Other' && !otherCollege.trim()) { setError('Please enter your college name'); return; }
        if (!major) { setError('Please select your major'); return; }
        if (major === 'Other' && !otherMajor.trim()) { setError('Please enter your major'); return; }
        if (!profilePhoto) { setError('Profile photo is required'); return; }
      } else {
        if (!companyName.trim()) { setError('Company name is required'); return; }
        if (!industry) { setError('Please select your industry'); return; }
        if (!companyLogo) { setError('Company logo is required'); return; }
      }
    }
    setError('');
    setStep(step + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      let photoURL = null;
      let logoURL = null;
      if (userType === 'student' && profilePhoto) {
        const photoRef = ref(storage, `profileImages/student/${user.uid}`);
        await uploadBytes(photoRef, profilePhoto);
        photoURL = await getDownloadURL(photoRef);
      }
      if (userType === 'company' && companyLogo) {
        const logoRef = ref(storage, `profileImages/company/${user.uid}`);
        await uploadBytes(logoRef, companyLogo);
        logoURL = await getDownloadURL(logoRef);
      }
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        userType,
        createdAt: new Date(),
        profileCompleted: true
      });
      if (userType === 'student') {
        const finalCollege = college === 'Other' ? otherCollege : college;
        const finalMajor = major === 'Other' ? otherMajor : major;
        await setDoc(doc(db, 'studentProfiles', user.uid), {
          fullName,
          college: finalCollege,
          major: finalMajor,
          photoURL,
          bio: '',
          skills: [],
          resume: null,
          portfolio: null,
          activeProjects: 0,
          completedProjects: 0
        });
      } else {
        await setDoc(doc(db, 'companyProfiles', user.uid), {
          companyName,
          industry,
          companyLogo: logoURL,
          companyDescription: '',
          companyWebsite: '',
          location: '',
          companySize: ''
        });
      }
      setTimeout(() => {
        navigate(userType === 'student' ? '/student/dashboard' : '/company/dashboard', { replace: true });
      }, 300);
    } catch (err) {
      setError(err.message || 'Failed to create account');
      setLoading(false);
    }
  };

  const inputStyle = { width: '100%', padding: '12px 15px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '16px' };

  return (
    <>
      <Navigation />
      <div className="container" style={{ maxWidth: '600px', margin: '60px auto', padding: '0 20px' }}>
        <div style={{ background: 'white', borderRadius: '16px', padding: '40px', boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }}>
          <h2 style={{ textAlign: 'center', marginBottom: '30px' }}>Create Your Synked Account</h2>
          <div style={{ marginBottom: '30px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative' }}>
              {[1, 2, 3].map((s) => (
                <div key={s} style={{
                  width: 30, height: 30, borderRadius: '50%',
                  background: step >= s ? 'var(--primary)' : '#eee',
                  color: step >= s ? 'white' : '#666',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', zIndex: 2
                }}>{s}</div>
              ))}
              <div style={{ position: 'absolute', top: 15, left: 30, right: 30, height: 2, background: '#eee', zIndex: 1 }}>
                <div style={{ width: step === 1 ? '0%' : step === 2 ? '50%' : '100%', height: '100%', background: 'var(--primary)', transition: 'width 0.3s' }} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, fontSize: 14 }}>
              <span style={{ width: 60, textAlign: 'center' }}>Account</span>
              <span style={{ width: 60, textAlign: 'center' }}>Profile</span>
              <span style={{ width: 60, textAlign: 'center' }}>Finish</span>
            </div>
          </div>
          {error && (
            <div style={{ color: 'var(--danger)', backgroundColor: 'rgba(239, 68, 68, 0.1)', padding: '12px 15px', borderRadius: 8, marginBottom: 20, fontSize: 14 }}>{error}</div>
          )}

          <form onSubmit={(e) => { e.preventDefault(); if (step === 3) handleSubmit(e); else handleNextStep(); }}>
            {step === 1 && (
              <div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>Account Type</label>
                  <div style={{ padding: 15, borderRadius: 8, border: '2px solid var(--primary)', backgroundColor: 'rgba(108, 99, 255, 0.05)', textAlign: 'center' }}>
                    {userType === 'student' ? 'Student Account' : 'Company Account'}
                  </div>
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>Email</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required style={inputStyle} />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>Password</label>
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} style={inputStyle} />
                  <p style={{ fontSize: 14, color: '#666', marginTop: 5 }}>At least 6 characters</p>
                </div>
                <div style={{ marginBottom: 30 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>Confirm Password</label>
                  <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={6} style={inputStyle} />
                </div>
                <button type="button" onClick={handleNextStep} className="btn btn-primary" style={{ width: '100%' }}>Continue</button>
              </div>
            )}

            {step === 2 && (
              <div>
                {userType === 'student' ? (
                  <>
                    <div style={{ marginBottom: 20 }}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>Full Name</label>
                      <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} style={inputStyle} />
                    </div>
                    <div style={{ marginBottom: 20 }}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>College/University</label>
                      <select value={college} onChange={(e) => setCollege(e.target.value)} style={{ ...inputStyle, backgroundColor: 'white' }}>
                        <option value="">Select your college</option>
                        {colleges.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    {college === 'Other' && (
                      <div style={{ marginBottom: 20 }}>
                        <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>Other College</label>
                        <input type="text" value={otherCollege} onChange={(e) => setOtherCollege(e.target.value)} style={inputStyle} />
                      </div>
                    )}
                    <div style={{ marginBottom: 20 }}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>Major</label>
                      <select value={major} onChange={(e) => setMajor(e.target.value)} style={{ ...inputStyle, backgroundColor: 'white' }}>
                        <option value="">Select your major</option>
                        {majors.map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </div>
                    {major === 'Other' && (
                      <div style={{ marginBottom: 20 }}>
                        <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>Other Major</label>
                        <input type="text" value={otherMajor} onChange={(e) => setOtherMajor(e.target.value)} style={inputStyle} />
                      </div>
                    )}
                    <div style={{ marginBottom: 30 }}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>Profile Photo</label>
                      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                        <div style={{ width: 100, height: 100, borderRadius: '50%', background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', border: '1px solid #ddd' }}>
                          {photoPreview ? <img src={photoPreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ color: '#888', fontSize: 14 }}>No Photo</span>}
                        </div>
                        <div style={{ flex: 1 }}>
                          <input type="file" accept="image/*" onChange={handlePhotoChange} style={{ width: '100%' }} />
                          <p style={{ fontSize: 14, color: '#666', marginTop: 5 }}>Optional — add later if you skip</p>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ marginBottom: 20 }}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>Company Name</label>
                      <input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} style={inputStyle} />
                    </div>
                    <div style={{ marginBottom: 20 }}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>Industry</label>
                      <select value={industry} onChange={(e) => setIndustry(e.target.value)} style={{ ...inputStyle, backgroundColor: 'white' }}>
                        <option value="">Select your industry</option>
                        {industries.map((ind) => <option key={ind} value={ind}>{ind}</option>)}
                      </select>
                    </div>
                    <div style={{ marginBottom: 30 }}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>Company Logo</label>
                      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                        <div style={{ width: 100, height: 100, borderRadius: 12, background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', border: '1px solid #ddd' }}>
                          {logoPreview ? <img src={logoPreview} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <span style={{ color: '#888', fontSize: 14 }}>No Logo</span>}
                        </div>
                        <div style={{ flex: 1 }}>
                          <input type="file" accept="image/*" onChange={handleLogoChange} style={{ width: '100%' }} />
                          <p style={{ fontSize: 14, color: '#666', marginTop: 5 }}>Optional — add later if you skip</p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                <div style={{ display: 'flex', gap: 15, flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => setStep(1)} className="btn btn-outline" style={{ flex: 1 }}>Back</button>
                  <button type="button" onClick={handleSkip} disabled={loading} className="btn btn-outline" style={{ flex: 1 }}>
                    {loading ? 'Creating...' : 'Skip for now'}
                  </button>
                  <button type="button" onClick={handleNextStep} className="btn btn-primary" style={{ flex: 1 }}>Continue</button>
                </div>
                <p style={{ fontSize: 13, color: '#888', marginTop: 12, textAlign: 'center' }}>Skip to create your account and add profile details later.</p>
              </div>
            )}

            {step === 3 && (
              <div>
                <h3 style={{ marginBottom: 20, fontWeight: 600 }}>Review</h3>
                <div style={{ marginBottom: 25, padding: 20, backgroundColor: '#f9f9f9', borderRadius: 8 }}>
                  <div style={{ marginBottom: 10 }}><strong>Account:</strong> {email}</div>
                  <div style={{ marginBottom: 10 }}><strong>Type:</strong> {userType === 'student' ? 'Student' : 'Company'}</div>
                  {userType === 'student' && <div><strong>Name:</strong> {fullName} · {college === 'Other' ? otherCollege : college} · {major === 'Other' ? otherMajor : major}</div>}
                  {userType === 'company' && <div><strong>Company:</strong> {companyName} · {industry}</div>}
                </div>
                <div style={{ display: 'flex', gap: 15 }}>
                  <button type="button" onClick={() => setStep(2)} className="btn btn-outline" style={{ flex: 1 }}>Back</button>
                  <button type="submit" disabled={loading} className="btn btn-primary" style={{ flex: 1 }}>{loading ? 'Creating account...' : 'Create account'}</button>
                </div>
              </div>
            )}
          </form>

          <div style={{ marginTop: 30, textAlign: 'center', borderTop: '1px solid #eee', paddingTop: 20 }}>
            Already have an account? <Link to="/login" style={{ color: 'var(--primary)' }}>Log in</Link>
            <div style={{ marginTop: 10 }}>
              <Link to={`/register?type=${userType === 'student' ? 'company' : 'student'}`} style={{ color: 'var(--primary)', fontSize: 14 }}>
                Sign up as {userType === 'student' ? 'company' : 'student'} instead
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Register;
